import { Injectable, inject } from '@angular/core';
import { Api, isOKResponse } from './api';
import { AuthService } from './auth';
import { ProfileService } from './profile.service';
import { PromiseCache } from './promise-cache';

export interface HasPermission {
    has: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class SecurityService {

    private api = inject(Api);
    private auth = inject(AuthService);
    private profileService = inject(ProfileService);
    private promiseCache = inject(PromiseCache);

    async hasPermission(permission: string): Promise<boolean> {
        try {
            const rolesForPermission = await this.getProfileRolesForPermission(permission);
            return rolesForPermission.length > 0;
        } catch (error) {
            console.error("Error checking permission - trying to call API", permission, error);
            const result = await this.api.get<HasPermission>(`/security/has_permission/${permission}`);
            if (isOKResponse(result)) {
                return result.data.has;
            }
            return false;
        }
    }

    async hasPermissionFor(permission: string, user: string): Promise<boolean> {
        // Check if the user has the permission before checking if the permission is for a user
        const rolesForPermission = await this.getProfileRolesForPermission(permission);
        if (rolesForPermission.length === 0) {
            return false;
        };
        const result = await this.api.get<HasPermission>(`/security/has_permission_for/${permission}/${user}`);
        if (isOKResponse(result)) {
            return result.data.has;
        }
        return false;
    }

    async hasOwnPermissionFor(permission: string, user: string): Promise<boolean> {
        if (user === this.auth.currentUser()) {
            return await this.hasPermission(`${permission}.own`);
        }
        return await this.hasPermissionFor(permission, user);
    }

    async hasRole(role: string): Promise<boolean> {
        const profileRoles = await this.getProfileRoles();
        if (isOKResponse(profileRoles)) {
            return profileRoles.data.some(r => r._id === role);
        }

        const result = await this.api.get<HasPermission>(`/security/has_role/${role}`);
        if (isOKResponse(result)) {
            return result.data.has;
        }
        return false;
    }

    // userHasRole(user: string, role: string): boolean {
    //     return true;
    // }

    async hasRoleFor(user: string, role: string): Promise<boolean> {
        const result = await this.api.get<HasPermission>(`/security/has_role_for/${role}/${user}`);
        if (isOKResponse(result)) {
            return result.data.has;
        }
        return false;
    }

    async getProfileRoles() {
        return this.promiseCache.getOrCreate('profile-roles', 5 * 60 * 1000, () => this.profileService.getRoles());
    }

    async getProfileRolesForPermission(permission: string) {
        const permissions = await this.rolesToPermissions();
        const allPermissionRoles = permissions['system:all'] || [];
        const permissionRoles = permissions[permission] || [];
        return [...allPermissionRoles, ...permissionRoles];
    }

    async rolesToPermissions(): Promise<{[key: string]: string[]}> {
        return this.promiseCache.getOrCreate<{[key: string]: string[]}>(
            'roles-to-permissions',
            5 * 60 * 1000,
            () => this.buildRolesToPermissionsMap()
        );
    }

    private async buildRolesToPermissionsMap(): Promise<{[key: string]: string[]}> {
        const roles = await this.getProfileRoles();

        if (!isOKResponse(roles)) {
            return {};
        }

        const permissionsByKey: {[key: string]: string[]} = {};

        for (const role of roles.data) {
            const roleId = role._id;
            const rolePermissions = role.permissions || [];

            for (const permission of rolePermissions) {
                if (!permissionsByKey[permission]) {
                    permissionsByKey[permission] = [];
                }

                permissionsByKey[permission].push(roleId);
            }
        }

        return permissionsByKey;
    }
}
